import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { EmptyState } from '@/components/shared/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card/Card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { GitCompare, XCircle } from 'lucide-react';

type ReferenceSelectorProps = {
  comparisonOptions: string[];
  selectedBaseReference: string | null;
  selectedComparisonReferences: string[];
  isLoading: boolean;
  onBaseReferenceChange: (value: string | null) => void;
  onToggleComparisonReference: (reference: string) => void;
};

export function ReferenceSelector({
  comparisonOptions,
  selectedBaseReference,
  selectedComparisonReferences,
  isLoading,
  onBaseReferenceChange,
  onToggleComparisonReference,
}: ReferenceSelectorProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  // Available options for comparison (excluding base reference)
  const availableComparisonOptions = comparisonOptions.filter(
    (option) => option !== selectedBaseReference
  );

  if (isLoading && !comparisonOptions.length) {
    return (
      <Card as="section" padding="lg">
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-12 w-full max-w-md" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Card>
    );
  }

  return (
    <Card as="section" padding="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <GitCompare className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="section-title">{t('analytics.comparison.selector.title')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('analytics.comparison.selector.description')}
            </p>
          </div>
        </div>

        {/* Base Reference Selector */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="comparison-base-reference" className="text-sm font-medium">
              {t('analytics.comparison.selector.baseReference.label')}
            </Label>
            <Badge tone="primary" dot>
              {t('analytics.comparison.selector.baseReference.badge')}
            </Badge>
          </div>
          <SimpleSelect
            id="comparison-base-reference"
            value={selectedBaseReference ?? comparisonOptions[0] ?? ''}
            onChange={onBaseReferenceChange}
            options={comparisonOptions.map((reference) => ({
              value: reference,
              label: resolve(reference).label,
            }))}
            placeholder={t('analytics.comparison.selector.baseReference.placeholder')}
            disabled={isLoading || !comparisonOptions.length}
            className="max-w-md"
          />
          <p className="text-xs text-muted-foreground">
            {t('analytics.comparison.selector.baseReference.helper')}
          </p>
        </div>

        {/* Comparison References */}
        {selectedBaseReference && availableComparisonOptions.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                {t('analytics.comparison.selector.comparisonReferences.label')}
              </label>
              <span className="text-xs text-muted-foreground">
                {t('analytics.comparison.selector.comparisonReferences.count', {
                  selected: selectedComparisonReferences.length,
                  total: availableComparisonOptions.length,
                })}
              </span>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {availableComparisonOptions.map((reference) => {
                const isSelected = selectedComparisonReferences.includes(reference);

                return (
                  <button
                    key={reference}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggleComparisonReference(reference)}
                    disabled={isLoading}
                    className={cn(
                      'group relative flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-all',
                      'outline-none hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50',
                      isSelected
                        ? 'border-primary/60 bg-primary/10 text-primary shadow-sm'
                        : 'border-border bg-card text-foreground hover:border-primary/30'
                    )}
                  >
                    <span className="truncate">{resolve(reference).label}</span>
                    {isSelected && (
                      <div className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <svg
                          aria-hidden="true"
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground">
              {t('analytics.comparison.selector.comparisonReferences.helper')}
            </p>
          </div>
        )}

        {/* Selected References Summary */}
        {selectedBaseReference && selectedComparisonReferences.length > 0 && (
          <Card tier="inset" padding="sm">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  {t('analytics.comparison.selector.summary.title', {
                    count: selectedComparisonReferences.length + 1,
                  })}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge tone="primary" dot>
                    {resolve(selectedBaseReference).label}
                  </Badge>
                  {selectedComparisonReferences.map((ref) => (
                    <Badge key={ref}>
                      {resolve(ref).label}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleComparisonReference(ref);
                        }}
                        className="ml-1 rounded-full outline-none hover:opacity-70 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        aria-label={t('common.removeItem', { item: resolve(ref).label })}
                      >
                        <XCircle className="h-3 w-3" aria-hidden />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !comparisonOptions.length && (
          <EmptyState size="compact" title={t('analytics.comparison.selector.empty')} />
        )}
      </div>
    </Card>
  );
}
